import React, { useState, useEffect } from 'react';
import { Camera, Wifi, Settings, Plus, Monitor, Eye, EyeOff, AlertTriangle, Search, Zap, Activity, MapPin, Trash2, Play, Square, Loader2, ChevronDown, Filter, Users as UsersIcon, Shield, UserPlus, X, Flame, UserX, Car, Users, Clock, UserCheck } from 'lucide-react';
import { RestrictedAreaSetup } from './RestrictedAreaSetup';
import { IntrusionDetectionSetup } from './IntrusionDetectionSetup';
import ZoneSelector from './ZoneSelector';

interface CameraData {
  id: string;
  name: string;
  rtsp_url: string;
  location: string;
  status: 'active' | 'inactive';
  is_online: boolean;
  assigned_usecase: string[] | string | null;
  active_usecase: string[];
  detection_active: boolean;
  created_at: string;
  subuser_access?: {
    [email: string]: {
      access_type: 'camera' | 'location' | 'usecase';
      permissions: string[];
    };
  };
}

interface UseCase {
  id: string;
  name: string;
  description: string;
  category: 'default' | 'custom';
  is_active: boolean;
  created_at?: string;
}

interface Stats {
  total: number;
  active: number;
  online: number;
  detecting: number;
}

const UseCaseSelectionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  camera: CameraData | null;
  onStartDetection: (cameraId: string, usecase: string) => Promise<void>;
  onStopDetection: (cameraId: string, usecase: string) => Promise<void>;
}> = ({ isOpen, onClose, camera, onStartDetection, onStopDetection }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [processingUsecases, setProcessingUsecases] = useState<Set<string>>(new Set());
  const [showRestrictedAreaSetup, setShowRestrictedAreaSetup] = useState(false);
  const [showRestrictedAreaPHSetup, setShowRestrictedAreaPHSetup] = useState(false);
  const [showParkingZoneSetup, setShowParkingZoneSetup] = useState(false);
  const [zoneIntelligenceSetup, setZoneIntelligenceSetup] = useState<string | null>(null);
  const [showIntrusionSetup, setShowIntrusionSetup] = useState(false);
  const [intrusionDetectionType, setIntrusionDetectionType] = useState<'intrusion-detection' | 'zone-intrusion-detection'>('intrusion-detection');
  const [intrusionConfigured, setIntrusionConfigured] = useState<{[key: string]: boolean}>({});
  const [availableUseCases, setAvailableUseCases] = useState<UseCase[]>([]);
  const [loadingUseCases, setLoadingUseCases] = useState(false);

  const defaultUseCases: UseCase[] = [
    { id: 'fire-detection', name: 'Fire & Smoke Detection', description: 'Detects fire and smoke in real-time for immediate emergency response', category: 'default', is_active: true },
    { id: 'fall-detection', name: 'Fall Detection', description: 'Monitors for human falls and accidents, critical for elderly care', category: 'default', is_active: true },
    { id: 'anpr', name: 'License Plate Recognition', description: 'Automatic number plate recognition for vehicle identification', category: 'default', is_active: true },
    { id: 'worker-time-tracking', name: 'Worker Time Tracking', description: 'Track worker entry/exit times using license plate recognition', category: 'default', is_active: true },
    { id: 'crowd-detection', name: 'Crowd Detection', description: 'Monitors crowd density and detects overcrowding situations', category: 'default', is_active: true },
    { id: 'restricted-area', name: 'Restricted Area Detection', description: 'Detects unauthorized access to restricted zones', category: 'default', is_active: true },
    { id: 'restricted-area-ph', name: 'Restricted Area - PH', description: 'Advanced restricted area detection with polygon zones', category: 'default', is_active: true },
    { id: 'parking-analysis', name: 'Parking Management', description: 'Monitor parking zones and detect violations with zone-based analysis', category: 'default', is_active: true },
    { id: 'theft-detection', name: 'Theft Detection', description: 'Detects potential theft by tracking people near vehicles', category: 'default', is_active: true },
    { id: 'enhanced-loitering', name: 'Enhanced Loitering Detection', description: 'Advanced detection of people staying in areas too long', category: 'default', is_active: true },
    { id: 'intrusion-detection', name: 'General Intrusion Detection (After Hours)', description: 'Detects any person during after-hours with timezone support - no zones required', category: 'default', is_active: true },
    { id: 'zone-intrusion-detection', name: 'Zone-Based Intrusion Detection (After Hours)', description: 'Detects people entering specific protected zones during after-hours with timezone support', category: 'default', is_active: true },
    { id: 'loitering-intrusion', name: 'Loitering & Intrusion Detection', description: 'Detects people staying too long inside a zone you draw', category: 'default', is_active: true },
    { id: 'entry-exit-counting', name: 'Entry/Exit Counting', description: 'Counts people crossing a line you draw, in and out', category: 'default', is_active: true },
    { id: 'instore-foot-traffic', name: 'In-Store Foot Traffic', description: 'Alerts when occupancy inside a zone exceeds a threshold', category: 'default', is_active: true },
    { id: 'vehicle-suspicious-zone', name: 'Vehicle & Suspicious Zone Watch', description: 'Watches a zone for people and vehicles exceeding a threshold', category: 'default', is_active: true },
    { id: 'kitchen-safety', name: 'Kitchen Safety / PPE Detection', description: 'Detects missing gloves, hairnets, or masks in food prep areas', category: 'default', is_active: true },
    { id: 'staff-attendance', name: 'Staff Face Recognition & Attendance', description: 'Recognizes enrolled staff and logs attendance check-ins (enroll staff in the Staff tab)', category: 'default', is_active: true },
    { id: 'personal-vehicle-watch', name: 'Personal Vehicle Watch', description: "Watches for registered personal vehicles and logs when they're taken and parked", category: 'default', is_active: true }
  ];

  useEffect(() => {
    if (isOpen && camera) {
      fetchAvailableUseCases();
      checkIntrusionConfiguration();
    }
  }, [isOpen, camera]);

  const fetchAvailableUseCases = async () => {
    setLoadingUseCases(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const userResponse = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userData = await userResponse.json();
      const response = await fetch(`/api/usecases/available/${userData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableUseCases(data.usecases || defaultUseCases);
      }
    } catch (error) {
      console.error('Failed to fetch available use cases:', error);
      setAvailableUseCases(defaultUseCases);
    } finally {
      setLoadingUseCases(false);
    }
  };

  const checkIntrusionConfiguration = async () => {
    if (!camera) return;
    try {
      const token = localStorage.getItem('csense_auth_token');
      const generalResponse = await fetch(`/api/detection/intrusion/config/${camera.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const zoneResponse = await fetch(`/api/detection/zone-intrusion/config/${camera.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let configured = false;
      if (generalResponse.ok) {
        const data = await generalResponse.json();
        configured = configured || (data.timezone !== undefined);
      }
      if (zoneResponse.ok) {
        const data = await zoneResponse.json();
        configured = configured || (data.timezone !== undefined);
      }
      setIntrusionConfigured(prev => ({ ...prev, [camera.id]: configured }));
    } catch (error) {
      console.error('Failed to check intrusion configuration:', error);
    }
  };

  const getUseCaseDisplayInfo = (usecaseId: string) => {
    const iconMap: { [key: string]: any } = {
      'fire-detection': { icon: Flame, color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30' },
      'fall-detection': { icon: UserX, color: 'text-orange-500', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30' },
      'anpr': { icon: Car, color: 'text-blue-500', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30' },
      'worker-time-tracking': { icon: Car, color: 'text-green-500', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/30' },
      'crowd-detection': { icon: Users, color: 'text-purple-500', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30' },
      'restricted-area': { icon: AlertTriangle, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/30', requiresSetup: true },
      'restricted-area-ph': { icon: AlertTriangle, color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', requiresSetup: true },
      'parking-analysis': { icon: Car, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/30', requiresSetup: true },
      'theft-detection': { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-600/10', borderColor: 'border-red-600/30' },
      'enhanced-loitering': { icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30' },
      'intrusion-detection': { icon: Clock, color: 'text-purple-500', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', requiresSetup: true },
      'zone-intrusion-detection': { icon: AlertTriangle, color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', requiresSetup: true },
      'loitering-intrusion': { icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', requiresSetup: true },
      'entry-exit-counting': { icon: Users, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/30', requiresSetup: true },
      'instore-foot-traffic': { icon: Users, color: 'text-purple-500', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', requiresSetup: true },
      'vehicle-suspicious-zone': { icon: Car, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', borderColor: 'border-indigo-500/30', requiresSetup: true },
      'kitchen-safety': { icon: Shield, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30' },
      'staff-attendance': { icon: UserCheck, color: 'text-teal-500', bgColor: 'bg-teal-500/10', borderColor: 'border-teal-500/30' },
      'personal-vehicle-watch': { icon: Car, color: 'text-cyan-500', bgColor: 'bg-cyan-500/10', borderColor: 'border-cyan-500/30' }
    };
    return iconMap[usecaseId] || { icon: AlertTriangle, color: 'text-gray-500', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/30' };
  };

  const toggleUsecase = async (usecaseId: string) => {
    if (!camera || loading || processingUsecases.has(usecaseId)) return;
    setLoading(true);
    setProcessingUsecases(prev => new Set([...prev, usecaseId]));
    try {
      const isActive = (camera.active_usecase || []).includes(usecaseId);
      if (isActive) {
        await onStopDetection(camera.id, usecaseId);
        setStatusMessage('Detection stopped successfully');
      } else {
        await onStartDetection(camera.id, usecaseId);
        setStatusMessage('Detection started successfully');
      }
    } catch (error) {
      console.error('Failed to toggle detection:', error);
      setStatusMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
      setTimeout(() => setProcessingUsecases(prev => {
        const next = new Set(prev);
        next.delete(usecaseId);
        return next;
      }), 2000);
    }
  };

  if (!isOpen || !camera) return null;

  const filteredUsecases = (availableUseCases.length > 0 ? availableUseCases : defaultUseCases).filter(uc =>
    uc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    uc.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-[#0a0b14]/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative">
        <div className="absolute top-0 w-full h-[2px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold tracking-tight text-white flex items-center gap-3">
                <Shield className="w-6 h-6 text-blue-400" />
                Select Detection Type
              </h3>
              <p className="text-blue-100/60 text-sm mt-1 font-light">
                Camera: <span className="text-blue-400 font-mono">{camera.name}</span>
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 bg-white/5 rounded-xl border border-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search detection types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 border border-white/10 rounded-xl bg-black/40 text-white placeholder-gray-500 font-light transition-all focus:ring-2 focus:ring-blue-500/50 outline-none"
            />
          </div>

          {loadingUseCases && (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          <div className="space-y-3">
            {filteredUsecases.map((usecase) => {
              const displayInfo = getUseCaseDisplayInfo(usecase.id);
              const Icon = displayInfo.icon;
              const isActive = (camera.active_usecase || []).includes(usecase.id);
              const isImplemented = (usecase as any).implemented !== false;
              return (
                <div key={usecase.id} className={`p-5 border-2 rounded-2xl transition-all group ${!isImplemented ? 'border-white/5 bg-white/[0.02] opacity-60' : isActive ? `${displayInfo.borderColor} ${displayInfo.bgColor} shadow-[0_0_20px_rgba(59,130,246,0.1)]` : 'border-white/5 bg-white/5 hover:border-white/20 hover:bg-white/10 shadow-sm'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${displayInfo.bgColor}`}>
                      <Icon className={`h-6 w-6 ${displayInfo.color}`} />
                    </div>
                    <div className="flex-1">
                      <h5 className="text-white font-semibold mb-1 tracking-tight flex items-center gap-2">
                        {usecase.name}
                        {!isImplemented && (
                          <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">Coming Soon</span>
                        )}
                      </h5>
                      <p className="text-blue-100/50 text-sm font-light leading-snug">{usecase.description}</p>
                    </div>
                    {!isImplemented ? null : (
                    <div className="flex gap-2">
                      {displayInfo.requiresSetup && (
                        <button
                          onClick={() => {
                            if (usecase.id === 'restricted-area-ph') setShowRestrictedAreaPHSetup(true);
                            else if (usecase.id === 'parking-analysis') setShowParkingZoneSetup(true);
                            else if (usecase.id === 'intrusion-detection' || usecase.id === 'zone-intrusion-detection') {
                              setIntrusionDetectionType(usecase.id);
                              setShowIntrusionSetup(true);
                            } else if (['loitering-intrusion', 'entry-exit-counting', 'instore-foot-traffic', 'vehicle-suspicious-zone'].includes(usecase.id)) {
                              setZoneIntelligenceSetup(usecase.id);
                            } else setShowRestrictedAreaSetup(true);
                          }}
                          className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all min-w-[100px] shadow-lg ${usecase.id === 'restricted-area-ph' ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/20' : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/20'}`}
                        >
                          Setup
                        </button>
                      )}
                      <button
                        onClick={() => toggleUsecase(usecase.id)}
                        disabled={loading || processingUsecases.has(usecase.id) || (displayInfo.requiresSetup && !intrusionConfigured[camera.id] && (usecase.id === 'intrusion-detection' || usecase.id === 'zone-intrusion-detection'))}
                        className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all min-w-[80px] shadow-lg ${isActive ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/20' : 'bg-blue-500 hover:bg-blue-600 text-white shadow-blue-500/20'} disabled:opacity-50`}
                      >
                        {processingUsecases.has(usecase.id) ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" /> : (isActive ? 'Stop' : 'Start')}
                      </button>
                    </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {statusMessage && (
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-xl text-sm font-light">
              <Activity className="w-4 h-4 inline mr-2 opacity-70" />
              {statusMessage}
            </div>
          )}

          <div className="flex gap-4 pt-10">
            {camera.active_usecase.length > 0 && (
              <button
                onClick={async () => {
                  setLoading(true);
                  try {
                    for (const uc of camera.active_usecase) await onStopDetection(camera.id, uc);
                    setStatusMessage('All detections stopped');
                  } finally { setLoading(false); }
                }}
                className="flex-1 py-3.5 px-6 bg-red-500/10 border border-red-500/20 text-red-400 font-bold rounded-xl hover:bg-red-500/20"
              >
                Stop All ({camera.active_usecase.length})
              </button>
            )}
            <button onClick={onClose} className="flex-1 py-3.5 px-6 border border-white/10 text-gray-300 hover:bg-white/5 font-bold rounded-xl">Close</button>
          </div>
        </div>
      </div>

      {showRestrictedAreaSetup && <RestrictedAreaSetup onClose={() => setShowRestrictedAreaSetup(false)} />}
      
      {showRestrictedAreaPHSetup && (
        <div className="fixed inset-0 bg-[#0a0b14]/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-8">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-bold flex items-center gap-3"><MapPin className="text-red-500" /> Setup Restricted Zones</h3>
              <button onClick={() => setShowRestrictedAreaPHSetup(false)} className="text-gray-400 hover:text-white p-2 bg-white/5 rounded-xl border border-white/10"><X /></button>
            </div>
            <ZoneSelector cameraId={camera.id} zoneType="restricted" onSave={() => { setShowRestrictedAreaPHSetup(false); toggleUsecase('restricted-area-ph'); }} />
          </div>
        </div>
      )}

      {showParkingZoneSetup && (
        <div className="fixed inset-0 bg-[#0a0b14]/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-8">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-bold flex items-center gap-3"><Car className="text-indigo-500" /> Setup Parking Zones</h3>
              <button onClick={() => setShowParkingZoneSetup(false)} className="text-gray-400 hover:text-white p-2 bg-white/5 rounded-xl border border-white/10"><X /></button>
            </div>
            <ZoneSelector cameraId={camera.id} zoneType="parking" onSave={() => { setShowParkingZoneSetup(false); toggleUsecase('parking-analysis'); }} />
          </div>
        </div>
      )}

      {zoneIntelligenceSetup && (
        <div className="fixed inset-0 bg-[#0a0b14]/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-8">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-bold flex items-center gap-3">
                <MapPin className="text-amber-500" />
                Setup {defaultUseCases.find(uc => uc.id === zoneIntelligenceSetup)?.name}
              </h3>
              <button onClick={() => setZoneIntelligenceSetup(null)} className="text-gray-400 hover:text-white p-2 bg-white/5 rounded-xl border border-white/10"><X /></button>
            </div>
            <ZoneSelector
              cameraId={camera.id}
              zoneType={zoneIntelligenceSetup}
              onSave={() => { const id = zoneIntelligenceSetup; setZoneIntelligenceSetup(null); toggleUsecase(id); }}
            />
          </div>
        </div>
      )}

      {showIntrusionSetup && (
        <IntrusionDetectionSetup
          camera={camera}
          detectionType={intrusionDetectionType}
          onClose={() => setShowIntrusionSetup(false)}
          onSave={async (config) => {
            const token = localStorage.getItem('csense_auth_token');
            const endpoint = intrusionDetectionType === 'zone-intrusion-detection' ? `/api/detection/zone-intrusion/configure/${camera.id}` : `/api/detection/intrusion/configure/${camera.id}`;
            const res = await fetch(endpoint, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(config) });
            if (res.ok) { setIntrusionConfigured(prev => ({ ...prev, [camera.id]: true })); toggleUsecase(intrusionDetectionType); }
            setShowIntrusionSetup(false);
          }}
        />
      )}
    </div>
  );
};

const AddCameraModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onAdd: (camera: { name: string; rtsp_url: string; location: string }) => void;
}> = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({ name: '', rtsp_url: '', location: '' });
  const [locations, setLocations] = useState<any[]>([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  useEffect(() => { if (isOpen) fetchLocations(); }, [isOpen]);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('csense_auth_token');
      const res = await fetch('/api/locations', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setLocations(data.locations || []); }
    } catch (e) { console.error(e); }
  };

  const createLocation = async () => {
    if (!newLocationName.trim()) return;
    try {
      const token = localStorage.getItem('csense_auth_token');
      const res = await fetch('/api/locations', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ name: newLocationName }) });
      if (res.ok) { setFormData({ ...formData, location: newLocationName }); setNewLocationName(''); setShowLocationModal(false); fetchLocations(); }
    } catch (e) { console.error(e); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#0a0b14]/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-3xl max-w-md w-full shadow-2xl relative p-8">
        <h3 className="text-2xl font-bold mb-8">Initialize Node</h3>
        <div className="space-y-6">
          <input placeholder="Node Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500/50" />
          <input placeholder="RTSP Stream URI" value={formData.rtsp_url} onChange={e => setFormData({ ...formData, rtsp_url: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500/50 font-mono text-sm" />
          <div className="relative">
            <input placeholder="Location" value={formData.location} onFocus={() => setShowLocationDropdown(true)} onChange={e => setFormData({ ...formData, location: e.target.value })} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 placeholder-gray-600 outline-none focus:ring-2 focus:ring-blue-500/50" />
            {showLocationDropdown && (
              <div className="absolute z-10 w-full mt-2 bg-[#1a1b26] border border-white/10 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                {locations.filter(l => l.name.toLowerCase().includes(formData.location.toLowerCase())).map(l => (
                  <button key={l.id} onClick={() => { setFormData({ ...formData, location: l.name }); setShowLocationDropdown(false); }} className="w-full text-left px-4 py-2 hover:bg-white/5 text-sm">{l.name}</button>
                ))}
                <button onClick={() => { setShowLocationModal(true); setShowLocationDropdown(false); }} className="w-full text-left px-4 py-2 hover:bg-white/5 text-blue-400 text-sm font-bold border-t border-white/5">+ Add New</button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-10 flex flex-col gap-3">
          <button onClick={() => { onAdd(formData); onClose(); }} className="w-full bg-blue-500 font-bold py-4 rounded-2xl shadow-lg shadow-blue-500/20">Connect Node</button>
          <button onClick={onClose} className="w-full border border-white/10 text-gray-400 py-4 rounded-2xl">Cancel</button>
        </div>
      </div>
      {showLocationModal && (
        <div className="fixed inset-0 bg-[#0a0b14]/95 z-[60] flex items-center justify-center p-4">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl p-8 max-w-sm w-full">
            <h3 className="text-xl font-bold mb-6">New Location</h3>
            <input value={newLocationName} onChange={e => setNewLocationName(e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 mb-6" />
            <button onClick={createLocation} className="w-full bg-blue-500 font-bold py-4 rounded-2xl mb-3">Create</button>
            <button onClick={() => setShowLocationModal(false)} className="w-full border border-white/10 py-4 rounded-2xl">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const VideoStreamModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  camera: CameraData | null;
}> = ({ isOpen, onClose, camera }) => {
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setError(false);
    setReloadKey(0);
  }, [camera?.id]);

  if (!isOpen || !camera) return null;

  const token = localStorage.getItem('csense_auth_token');
  const streamSrc = `/api/cameras/${camera.id}/live-stream?token=${encodeURIComponent(token || '')}&r=${reloadKey}`;

  const retry = () => {
    setError(false);
    setReloadKey(k => k + 1);
  };

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-xl flex items-center justify-center z-50">
      <div className="bg-white/5 backdrop-blur-2xl border border-white/10 text-white rounded-2xl max-w-4xl w-full mx-4 shadow-2xl overflow-hidden relative p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold tracking-tight">Live Intercept - <span className="text-cyan-400">{camera.name}</span></h3>
          <button onClick={onClose} className="p-2 bg-white/5 rounded-xl border border-white/10"><X /></button>
        </div>
        <div className="aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl relative border border-white/10">
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6">
              <p className="text-red-400 text-sm">Camera is unreachable right now.</p>
              <button onClick={retry} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest">Retry</button>
            </div>
          ) : (
            <>
              <div className="absolute top-0 w-full h-[2px] bg-cyan-500/30 animate-scan z-10" />
              <img key={reloadKey} src={streamSrc} className="w-full h-full object-cover" onError={() => setError(true)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CameraThumbnail: React.FC<{ cameraId: string; isOnline: boolean }> = ({ cameraId, isOnline }) => {
  // Uses /api/cameras/{id}/latest-frame, which only reads whatever frame the
  // camera's detection frame-pump already captured - no new RTSP connection per
  // poll (that's what broke detection before: see git history on this component).
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isOnline) return;
    let currentUrl: string | null = null;
    let cancelled = false;

    const fetchLatestFrame = async () => {
      try {
        const token = localStorage.getItem('csense_auth_token');
        const res = await fetch(`/api/cameras/${cameraId}/latest-frame`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return; // no detection running yet for this camera - keep placeholder
        const data = await res.json();
        if (cancelled || !data.frame) return;
        const url = `data:image/jpeg;base64,${data.frame}`;
        const previousUrl = currentUrl;
        currentUrl = url;
        setImgUrl(url);
        void previousUrl; // data: URLs don't need revoking, unlike blob: URLs
      } catch {
        // keep showing the last good frame (or the placeholder) on failure
      }
    };

    fetchLatestFrame();
    const interval = setInterval(fetchLatestFrame, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [cameraId, isOnline]);

  if (imgUrl) {
    return <img src={imgUrl} className="w-full h-full object-cover" alt="" />;
  }
  return <Camera className="w-16 h-16 text-white/10" />;
};

const CamerasSimplified: React.FC = () => {
  const [cameras, setCameras] = useState<CameraData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCamera, setSelectedCamera] = useState<CameraData | null>(null);
  const [showUseCaseModal, setShowUseCaseModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVideoStream, setShowVideoStream] = useState(false);
  const [streamingCamera, setStreamingCamera] = useState<CameraData | null>(null);
  const [isProcessingDetection, setIsProcessingDetection] = useState<{ [key: string]: boolean }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({ total: 0, active: 0, online: 0, detecting: 0 });
  const [isSubuser, setIsSubuser] = useState(false);

  useEffect(() => { fetchCurrentUser(); fetchCameras(); }, []);

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('csense_auth_token');
    const res = await fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } });
    if (res.ok) { const data = await res.json(); setIsSubuser(data.user_type === 'subuser' || data.is_subuser || false); }
  };

  const fetchCameras = async () => {
    try {
      const token = localStorage.getItem('csense_auth_token');
      const res = await fetch('/api/cameras', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.cameras || []).map((c: any) => ({
          id: c._id || c.id,
          name: c.name,
          rtsp_url: c.rtsp_url,
          location: c.location,
          status: c.status,
          is_online: c.is_online || false,
          assigned_usecase: c.assigned_usecase,
          active_usecase: Array.isArray(c.active_usecase) ? c.active_usecase : (c.active_usecase && c.active_usecase !== 'none' ? [c.active_usecase] : []),
          detection_active: (c.active_usecase && c.active_usecase !== 'none' && (Array.isArray(c.active_usecase) ? c.active_usecase.length > 0 : true)),
          created_at: c.created_at
        }));
        setCameras(mapped);
        setStats({ total: mapped.length, active: mapped.filter((m:any) => m.status === 'active').length, online: mapped.filter((m:any) => m.is_online).length, detecting: mapped.filter((m:any) => m.detection_active).length });
      }
    } finally { setLoading(false); }
  };

  const addCamera = async (data: any) => {
    const token = localStorage.getItem('csense_auth_token');
    const res = await fetch('/api/cameras/add-simple', { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
    if (res.ok) fetchCameras();
  };

  const startDetection = async (id: string, uc: string) => {
    setIsProcessingDetection(prev => ({ ...prev, [id]: true }));
    const token = localStorage.getItem('csense_auth_token');
    const res = await fetch(`/api/cameras/${id}/assign-detection`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ detection_type: uc, action: 'start' }) });
    if (res.ok) fetchCameras();
    setIsProcessingDetection(prev => ({ ...prev, [id]: false }));
  };

  const stopDetection = async (id: string, uc: string) => {
    setIsProcessingDetection(prev => ({ ...prev, [id]: true }));
    const token = localStorage.getItem('csense_auth_token');
    await fetch(`/api/cameras/${id}/assign-detection`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ detection_type: uc, action: 'stop' }) });
    fetchCameras();
    setIsProcessingDetection(prev => ({ ...prev, [id]: false }));
  };

  const deleteCamera = async (id: string) => {
    if (!confirm('Are you sure?')) return;
    const token = localStorage.getItem('csense_auth_token');
    await fetch(`/api/cameras/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    fetchCameras();
  };

  const filteredCameras = cameras.filter(c => (c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.location.toLowerCase().includes(searchTerm.toLowerCase())) && (filterStatus === 'all' || (filterStatus === 'online' && c.is_online) || (filterStatus === 'offline' && !c.is_online) || (filterStatus === 'detecting' && c.detection_active))).sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return 0;
  });

  if (loading) return <div className="p-12 flex items-center justify-center min-h-[60vh]"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen p-6 relative">
      <div className="max-w-7xl mx-auto pb-20">
        <div className="mb-10 flex justify-between items-end">
          <div>
            <h1 className="text-4xl font-bold text-white tracking-tighter flex items-center gap-4"><Monitor className="w-10 h-10 text-blue-400" /> Camera Fleet</h1>
            <p className="text-blue-100/40 uppercase tracking-widest text-xs mt-2 pl-14">Infrastructure Management</p>
          </div>
          {!isSubuser && <button onClick={() => setShowAddModal(true)} className="bg-blue-500 text-white px-8 py-4 rounded-2xl font-bold shadow-lg shadow-blue-500/20 hover:scale-105 transition-all flex items-center gap-2"><Plus /> Deploy Node</button>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {[ { l: 'Nodes', v: stats.total, i: Camera, c: 'text-blue-400' }, { l: 'Online', v: stats.online, i: Zap, c: 'text-emerald-400' }, { l: 'Active', v: stats.detecting, i: Eye, c: 'text-amber-400' }, { l: 'Health', v: '100%', i: Activity, c: 'text-cyan-400' } ].map((s, idx) => (
            <div key={idx} className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
              <p className="text-blue-100/40 text-[10px] uppercase font-bold tracking-widest mb-1">{s.l}</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-white">{s.v}</span>
                <s.i className={`w-8 h-8 ${s.c}`} />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 mb-10 border border-white/10 flex gap-6 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Locate node..." className="w-full bg-black/40 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white outline-none focus:ring-2 focus:ring-blue-500/50" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-black/40 border border-white/10 rounded-xl px-6 py-4 text-white outline-none"><option value="all">All Status</option><option value="online">Online</option><option value="detecting">Detecting</option></select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {filteredCameras.map(c => (
            <div key={c.id} className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden group hover:-translate-y-1 transition-all">
              <div className="h-56 bg-black relative flex items-center justify-center">
                <CameraThumbnail cameraId={c.id} isOnline={c.is_online} />
                <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-widest border ${c.is_online ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                  {c.is_online ? 'Online' : 'Offline'}
                </div>
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button onClick={() => { setStreamingCamera(c); setShowVideoStream(true); }} className="bg-blue-500 w-14 h-14 rounded-full flex items-center justify-center text-white"><Play className="fill-current" /></button>
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{c.name}</h3>
                    <p className="text-blue-100/40 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</p>
                  </div>
                  {!isSubuser && <button onClick={() => deleteCamera(c.id)} className="text-gray-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
                <button onClick={() => { setSelectedCamera(c); setShowUseCaseModal(true); }} className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${c.detection_active ? 'bg-white/5 border border-white/10 text-white' : 'bg-blue-500 text-white'}`}>
                  {c.detection_active ? <><Settings className="w-4 h-4" /> Manage AI</> : <><Zap className="w-4 h-4 fill-current" /> Activate AI</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <UseCaseSelectionModal isOpen={showUseCaseModal} onClose={() => setShowUseCaseModal(false)} camera={selectedCamera} onStartDetection={startDetection} onStopDetection={stopDetection} />
      <AddCameraModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onAdd={addCamera} />
      <VideoStreamModal isOpen={showVideoStream} onClose={() => setShowVideoStream(false)} camera={streamingCamera} />
    </div>
  );
};

export default CamerasSimplified;